import { memo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from '@pulsex/localization'
import AnimatedDropdown from 'pages/Swap/components/AnimatedDropdown'
import { Column } from 'components/Layout/Column'
import { LoadingRows } from 'components/Loader/LoadingRows'
import RoutingDiagram from 'components/RoutingDiagram'
import { AutoRow, RowBetween } from 'components/Layout/Row'
import { Protocol } from '@pulsex/smart-order-router/src/routers/sdk'
import { Currency, PairV1, Percent, TradeType } from '@pulsex/sdk'
import { Box, Text, AddIcon } from '@pulsex/uikit'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { InterfaceTrade } from 'state/routing/types'
import { AutoRouterLabel, AutoRouterLogo } from './RouterLabel'
import { Separator } from '../../styles'

const Wrapper = styled(Column)<{ darkMode?: boolean; fixedOpen?: boolean }>`
  padding: ${({ fixedOpen }) => (fixedOpen ? '12px' : '12px 8px 12px 12px')};
  border-radius: 16px;
  border: 1px solid ${({ theme, fixedOpen }) => (fixedOpen ? 'transparent' : theme.colors.cardBorder)};
  cursor: pointer;
`

const OpenCloseIcon = styled(AddIcon)<{ open?: boolean }>`
  margin-left: 8px;
  height: 20px;
  stroke-width: 2px;
  transition: transform 0.1s;
  transform: ${({ open }) => (open ? 'rotate(45deg)' : 'none')};
  stroke: ${({ theme }) => theme.colors.textSubtle};
  cursor: pointer;
  :hover {
    opacity: 0.8;
  }
`

interface SwapRouteProps extends React.HTMLAttributes<HTMLDivElement> {
  trade: InterfaceTrade<Currency, Currency, TradeType>
  syncing: boolean
  fixedOpen?: boolean // fixed in open state, hide open/close icon
}

export default memo(function SwapRoute({ trade, syncing, fixedOpen = false, ...rest }: SwapRouteProps) {
  const { chainId } = useActiveWeb3React()
  const { t } = useTranslation()
  const routes = getTokenPath(trade)
  const [open, setOpen] = useState(false)

  const formattedGasPriceString = trade?.gasUseEstimateUSD
    ? trade.gasUseEstimateUSD.toFixed(2) === '0.00'
      ? '<$0.01'
      : `$${  trade.gasUseEstimateUSD.toFixed(2)}`
    : undefined

  return (
    <Wrapper {...rest} fixedOpen={fixedOpen}>
      <RowBetween onClick={() => setOpen(!open)}>
        <AutoRow gap="4px" width="auto">
          <AutoRouterLogo />
          <AutoRouterLabel />
        </AutoRow>
        {fixedOpen ? null : <OpenCloseIcon open={open} />}
      </RowBetween>
      <AnimatedDropdown open={open || fixedOpen}>
        <Box paddingTop="12px">
        {syncing ? (
            <Box paddingBottom="4px">
              <LoadingRows>
                <div style={{ width: '100%', height: '30px' }} />
              </LoadingRows>
            </Box>
        ) : (
          <Box paddingBottom="4px">
            <RoutingDiagram
            currencyIn={trade.inputAmount.currency}
            currencyOut={trade.outputAmount.currency}
            routes={routes}
            />
          </Box>
        )}
          <Separator />
          {syncing ? (
            <Box paddingTop="4px">
              <LoadingRows>
                <div style={{ width: '100%', height: '15px' }} />
              </LoadingRows>
            </Box>
          ) : (
            <Text small paddingTop="4px">
              {trade?.gasUseEstimateUSD && chainId ? (t(`Best price route costs ~${formattedGasPriceString} in gas.`)) : null}{' '}
              {t(`This route optimizes your total output by considering split routes, multiple hops, and the gas cost of each step.`)}
            </Text>
          )}
        </Box>
      </AnimatedDropdown>
    </Wrapper>
  )
})

export interface RoutingDiagramEntry {
  percent: Percent
  path: [Currency, Currency, string][]
  protocol: Protocol
}

const V1_PROTOCOL = 'V1'
const V2_PROTOCOL = 'V2'

/**
 * Loops through all routes on a trade and returns an array of diagram entries.
 */
export function getTokenPath(trade: InterfaceTrade<Currency, Currency, TradeType>): RoutingDiagramEntry[] {
  return trade?.swaps?.map(({ route: { path: tokenPath, pools, protocol }, inputAmount, outputAmount }) => {
    const portion =
      trade.tradeType === TradeType.EXACT_INPUT
        ? inputAmount.divide(trade.inputAmount)
        : outputAmount.divide(trade.outputAmount)
    const percent = new Percent(portion.numerator, portion.denominator)
    const path: RoutingDiagramEntry['path'] = []
    for (let i = 0; i < pools.length; i++) {
      const nextPool = pools[i]
      const tokenIn = tokenPath[i]
      const tokenOut = tokenPath[i + 1]
      const entry: RoutingDiagramEntry['path'][0] = [
        tokenIn,
        tokenOut,
        nextPool instanceof PairV1 ? V1_PROTOCOL : V2_PROTOCOL,
      ]
      path.push(entry)
    }
    return {
      percent,
      path,
      protocol,
    }
  })
}
